import cisFertigationDataConfig from '@/api/config/cisFertigationDataConfig'
import InputLabelField from '@/components/form-control/InputLabelField'
import Select from '@/components/form-control/Select'
import { useAuthContext } from '@/features/auth/context'
import { useConfigUI } from '@/features/config/context'
import useAddCisFertigation from '@/features/config/hooks/cisFertigation/useAddCisFertigation'
import useDetailCisFertigation from '@/features/config/hooks/cisFertigation/useDetailCisFertigation'
import useUpdateCisFertigation from '@/features/config/hooks/cisFertigation/useUpdateCisFertigation'
import useTranslation from '@/hooks/useTranslation'
import { DATA_PAGINATION } from '@/types'
import { yupResolver } from '@hookform/resolvers/yup'
import { LoadingButton } from '@mui/lab'
import {
  Button,
  CircularProgress,
  Container,
  Fade,
  Stack,
  Typography,
} from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'
import {
  KeyFormCisFertigation,
  OptionItem,
} from './CisFertigationView.constant'
import { IFormCisFertigation } from '@/types/cisFertigation'

interface CisFertigationViewProps {
  id?: string
  pipeId?: number
  isParallelMode?: boolean
}

const CisFertigationView = ({
  id,
  pipeId,
  isParallelMode,
}: CisFertigationViewProps) => {
  const { t } = useTranslation()
  const { closeSidebar } = useConfigUI()
  const { interfaceId } = useAuthContext()
  const { mutate: add, isLoading: loadingAdd } = useAddCisFertigation()

  const { mutate: edit, isLoading: loadingEdit } = useUpdateCisFertigation()
  const { data: dataItem, isFetching } = useDetailCisFertigation(id ?? '')
  const [dataCacheOption, setDataCacheOption] = useState<{
    [key in KeyFormCisFertigation]?: OptionItem[]
  }>({})

  const inputRefName = useRef<HTMLInputElement>(null)
  const schema = yup
    .object({
      [KeyFormCisFertigation.Id]: yup.mixed().nullable().optional(),
      [KeyFormCisFertigation.Name]: yup
        .string()
        .required(
          `${t('fertiliserName')} ${t('commonFormsValidationIsRequired')}`,
        ),
      [KeyFormCisFertigation.Flushing]: yup
        .object()
        .required(
          `${t('Injection Valve')} ${t('commonFormsValidationIsRequired')}`,
        ),
    })
    .required()

  const mode = id ? 'edit' : 'create'

  const isLoading = loadingAdd || loadingEdit

  const handleSave = (data: any) => {
    const formData: IFormCisFertigation = {
      interfaceId: interfaceId!,
      pipeId: pipeId!,
      name: data[KeyFormCisFertigation.Name],
      id: data[KeyFormCisFertigation.Id],
      fertiliserId: data[KeyFormCisFertigation.Fertiliser]?.id,
      agitatorId: data[KeyFormCisFertigation.Agitator]?.id,
      flushingId: data[KeyFormCisFertigation.Flushing]?.id,
      flowSensorId: data[KeyFormCisFertigation.FlowSensor]?.id,
      pumpStartId: data[KeyFormCisFertigation.PumpStart]?.id,
      waterLevelId: data[KeyFormCisFertigation.WaterLevel]?.id,
    }

    if (mode === 'create') {
      add(formData)
    }
    if (mode === 'edit') {
      edit(formData)
    }
  }

  const getOptions = (key: KeyFormCisFertigation) => async () => {
    let data: DATA_PAGINATION | undefined

    const dataOption = await getGetOption(key)
      .then((res) => res.data)
      .catch(() => [])
    const dataMapper = dataOption.map((it: OptionItem) => ({
      ...it,
      label: it.name,
    }))

    data = {
      data: dataMapper,
      page: 1,
      size: dataOption.length,
      totalCount: dataOption.length,
      totalPage: 1,
    }
    dataCacheOption[key] = dataOption
    setDataCacheOption(dataCacheOption)
    return data
  }

  const getGetOption = (key: KeyFormCisFertigation) => {
    if (dataCacheOption[key])
      return Promise.resolve({ data: dataCacheOption[key] })

    const interfaceIdStr = interfaceId!.toString()
    switch (key) {
      case KeyFormCisFertigation.Fertiliser:
        return cisFertigationDataConfig.getFertiliserOption(interfaceIdStr, id)
      case KeyFormCisFertigation.Agitator:
        return cisFertigationDataConfig.getAgitatorOption(interfaceIdStr, id)
      case KeyFormCisFertigation.Flushing:
        return cisFertigationDataConfig.getFlushingOption(interfaceIdStr, id)
      case KeyFormCisFertigation.FlowSensor:
        return cisFertigationDataConfig.getflowSensorOption(interfaceIdStr, id)
      case KeyFormCisFertigation.PumpStart:
        return cisFertigationDataConfig.getPumpStartOption(interfaceIdStr, id)
      case KeyFormCisFertigation.WaterLevel:
        return cisFertigationDataConfig.getWaterLevelSensorOption(
          interfaceIdStr,
          id,
        )
      default:
        return Promise.resolve({ data: [] })
    }
  }

  useEffect(() => {
    if (inputRefName.current) {
      inputRefName.current.focus()
    }
  }, [])

  const form = useForm({
    resolver: yupResolver(schema),
  })

  useEffect(() => {
    if (dataItem) {
      form.reset({
        [KeyFormCisFertigation.Id]: dataItem.id,
        [KeyFormCisFertigation.Name]: dataItem.name,
      })
    }
  }, [dataItem, form, id])

  const SELECT_KEY_MAP: {
    key: KeyFormCisFertigation
    objectField: keyof typeof dataItem
  }[] = [
    { key: KeyFormCisFertigation.Fertiliser, objectField: 'fertiliser' },
    { key: KeyFormCisFertigation.Agitator, objectField: 'agitator' },
    { key: KeyFormCisFertigation.Flushing, objectField: 'flushing' },
    { key: KeyFormCisFertigation.FlowSensor, objectField: 'flowSensor' },
    { key: KeyFormCisFertigation.PumpStart, objectField: 'pumpStart' },
    { key: KeyFormCisFertigation.WaterLevel, objectField: 'waterLevel' },
  ]

  useEffect(() => {
    if (!dataItem) return
    SELECT_KEY_MAP.forEach(({ key, objectField }) => {
      const item = dataItem[objectField] as any | undefined
      if (item) {
        form.setValue(key as any, {
          ...item,
          label: item.name,
        })
      }
    })
  }, [dataItem])

  return (
    <Container sx={{ maxWidth: '450px', height: '100%' }}>
      {isFetching && (
        <Stack
          width="377px"
          height="100%"
          justifyContent="center"
          alignItems="center"
        >
          <CircularProgress />
        </Stack>
      )}
      {!isFetching && (
        <Fade in={!isFetching}>
          <form onSubmit={form.handleSubmit(handleSave)}>
            <Stack sx={{ padding: '34px 18px' }}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ width: '377px' }}
                marginBottom={2}
              >
                <Typography
                  sx={{
                    fontWeight: '500',
                    lineHeight: '14px',
                    color: 'text.main',
                  }}
                >
                  {id
                    ? t('dataConfigEditFertigation')
                    : t('dataConfigAddFertigation')}
                </Typography>
                <Stack direction="row">
                  <Button
                    variant="secondary"
                    sx={{ marginRight: '7px', width: '85px', height: '26px' }}
                    onClick={() => closeSidebar()}
                  >
                    Cancel
                  </Button>

                  <LoadingButton
                    loading={isLoading}
                    variant="primary"
                    size="small"
                    sx={{ minWidth: '85px', height: '26px' }}
                    type="submit"
                  >
                    Save
                  </LoadingButton>
                </Stack>
              </Stack>
              {id && (
                <InputLabelField
                  name={KeyFormCisFertigation.Id}
                  form={form}
                  label="ID"
                  style={{
                    height: 50,
                    borderRadius: '4px',
                  }}
                  padding="8px 16px"
                  disabled
                />
              )}

              <InputLabelField
                name={KeyFormCisFertigation.Name}
                form={form}
                label={t('fertiliserName')}
                placeholder={t('fertiliserName')}
                inputRef={inputRefName}
                style={{
                  height: 50,
                  borderRadius: '4px',
                  marginTop: '8px',
                }}
                padding="8px 16px"
                isRequired
              />
              <Select
                required
                minWidth={'100%'}
                label={t('Injection Valve')}
                name={KeyFormCisFertigation.Flushing}
                bindLabel="label"
                bindKey="label"
                placeholder="Select one"
                form={form}
                getOptions={getOptions(KeyFormCisFertigation.Fertiliser)}
              />
              <Select
                minWidth={'100%'}
                label={t('flowSensor')}
                name={KeyFormCisFertigation.FlowSensor}
                bindLabel="label"
                bindKey="label"
                placeholder="Select one"
                form={form}
                getOptions={getOptions(KeyFormCisFertigation.FlowSensor)}
              />
              <Select
                minWidth={'100%'}
                label={t('agitatorValve')}
                name={KeyFormCisFertigation.Agitator}
                bindLabel="label"
                bindKey="label"
                placeholder="Select one"
                form={form}
                getOptions={getOptions(KeyFormCisFertigation.Agitator)}
              />
              <Select
                minWidth={'100%'}
                label={t('waterLevelSensor')}
                name={KeyFormCisFertigation.WaterLevel}
                bindLabel="label"
                bindKey="label"
                placeholder="Select one"
                form={form}
                getOptions={getOptions(KeyFormCisFertigation.WaterLevel)}
              />
              {isParallelMode && (
                <Select
                  minWidth={'100%'}
                  label={t('Inlet Valve')}
                  name={KeyFormCisFertigation.Fertiliser}
                  bindLabel="label"
                  bindKey="label"
                  placeholder="Select one"
                  form={form}
                  getOptions={getOptions(KeyFormCisFertigation.Fertiliser)}
                />
              )}
              {isParallelMode && (
                <Select
                  minWidth={'100%'}
                  label={t('Fertigation Pump')}
                  name={KeyFormCisFertigation.PumpStart}
                  bindLabel="label"
                  bindKey="label"
                  placeholder="Select one"
                  form={form}
                  getOptions={getOptions(KeyFormCisFertigation.PumpStart)}
                />
              )}
            </Stack>
          </form>
        </Fade>
      )}
    </Container>
  )
}

export default CisFertigationView
